import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTenantThemeConfig } from '../hooks/useTenantThemeConfig'
import { getImageUrl } from '../utils/cdnUtils'
import { canRoundAcceptBet, lotteryService, LotteryRound, PublicHuayType } from '../services/lotteryService'

// ✨ Motion Variants
const cardVariants = {
  hidden: { opacity: 0, y: 100, scale: 0.8 },
  visible: { opacity: 1, y: 0, scale: 1 },
  hover: { scale: 1.05, y: -5 },
}

// 🎯 YeekeeCard Component (ใช้หน่วยนาที + config wipWupMinutes จากแอดมิน)
interface YeekeeCardProps {
  round: LotteryRound
  index: number
  countdown: string
  navigate: any
  /** นาทีที่เหลือก่อนปิดรับ — แสดงวิบวับเมื่อน้อยกว่าค่านี้ (จากแอดมิน ตั้งค่าอื่นๆ) */
  urgentMinutesThreshold?: number
}

const YeekeeCard: React.FC<YeekeeCardProps> = ({ round, index, countdown, navigate, urgentMinutesThreshold = 15 }) => {
  const isOpen = canRoundAcceptBet(round)
  const betEndDate = new Date(round.betEndTime)
  const closeTime = betEndDate.toLocaleString('th-TH', { 
    timeZone: 'Asia/Bangkok',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
  
  // Extract round number from roundNumber (e.g., "LYK-20251103-001" -> "1")
  const roundNum = round.roundNumber.split('-')[2] || index + 1

  // 🔥 นาทีที่เหลือก่อนปิดรับ — แสดงวิบวับเมื่อน้อยกว่าค่าจาก config (สอดคล้อง user-modern)
  const now = new Date()
  const minutesRemaining = (betEndDate.getTime() - now.getTime()) / (1000 * 60)
  const isUrgent = isOpen && minutesRemaining <= urgentMinutesThreshold && minutesRemaining > 0

  // 🎨 Dynamic styling for urgent rounds
  const cardStyle: React.CSSProperties = {
    ...(isUrgent && {
      border: '3px solid #ffa500',
      boxShadow: '0 0 20px rgba(255, 165, 0, 0.6), 0 0 40px rgba(255, 165, 0, 0.4)',
      animation: 'pulse-glow 2s ease-in-out infinite',
    }),
  }

  return (
    <>
      <style>{`
        @keyframes pulse-glow {
          0%, 100% { 
            box-shadow: 0 0 20px rgba(255, 165, 0, 0.6), 0 0 40px rgba(255, 165, 0, 0.4);
          }
          50% { 
            box-shadow: 0 0 30px rgba(255, 165, 0, 0.8), 0 0 60px rgba(255, 165, 0, 0.6);
          }
        }
      `}</style>
      <motion.div
        className="col-6 col-md-4 col-lg-3 px-1 mt-2"
        initial="hidden"
        animate="visible"
        whileHover={isOpen ? "hover" : undefined}
        variants={cardVariants}
        transition={{ 
          duration: 0.6, 
          delay: 0.1 + index * 0.05,
          ease: [0.43, 0.13, 0.23, 0.96]
        }}
      >
        <a
          href="#"
          className="text-center position-relative h-100"
          style={{ 
            padding: '1px',
            display: 'block',
            textDecoration: 'none',
            cursor: 'pointer',
          }}
          onClick={(e) => {
            e.preventDefault()
            if (isOpen) {
              navigate('/lottery-terms', { state: { round } })
            } else {
              navigate('/yeekee-result', { state: { round } })
            }
          }}
        >
          <div 
            className={`${isOpen ? 'bgg-1' : 'bgc-4'} bdr-5 box-hover`}
            style={cardStyle}
          >
            {isOpen && (
              <div className="box-mark" style={isUrgent ? { backgroundColor: '#ff0000' } : {}}>
                <span>{betEndDate.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Bangkok' })}</span>
              </div>
            )}
            <div className={`px-1 py-1 t-regular ${isOpen ? 'c-white' : 'c-dark'}`}>
              <h5 style={{ fontWeight: isUrgent ? '700' : '400' }}>{countdown}</h5>
            </div>
            <div className="bgc-14 px-1 t-regular c-white">
              <h4 className="txt-hh">รอบที่ {roundNum}</h4>
            </div>
            <div className={`px-1 py-2 ${isOpen ? 'c-white' : 'c-dark'}`}>
              <small>ปิดรับ {closeTime}</small>
            </div>
          </div>
        </a>
      </motion.div>
    </>
  )
}

const YeekeePage: React.FC = () => {
  const { themeConfig } = useTenantThemeConfig()
  const navigate = useNavigate()
  /** นาทีที่เหลือก่อนปิดรับ — แสดงวิบวับหวยยี่กี (จากแอดมิน ตั้งค่าอื่นๆ) */
  const urgentMinutesThreshold = themeConfig?.settings?.wipWupMinutes ?? 15
  const [rounds, setRounds] = useState<LotteryRound[]>([])
  const [huayTypes, setHuayTypes] = useState<PublicHuayType[]>([])
  const [loading, setLoading] = useState(true)
  const [countdown, setCountdown] = useState<Record<string, string>>({})
  
  // Load Yeekee rounds
  const loadRounds = useCallback(async (cacheBust = false) => {
    try {
      setLoading(true)
      const todayRounds = await lotteryService.getTodayRounds(cacheBust)
      
      // Filter for Yeekee rounds only
      const yeekeeRounds = todayRounds.filter(round => 
        round.huayTypeName?.includes('ยี') || 
        round.huayName?.includes('ยี')
      )
      
      setRounds(yeekeeRounds)
      console.log('✅ Loaded Yeekee rounds:', yeekeeRounds.length)
    } catch (error) {
      console.error('❌ Error loading Yeekee rounds:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadHuayTypes = useCallback(async () => {
    try {
      const list = await lotteryService.getTenantHuayTypes({ limit: 100 })
      setHuayTypes(list)
    } catch (error) {
      console.error('❌ Error loading Yeekee huay types:', error)
    }
  }, [])

  useEffect(() => {
    loadHuayTypes()
    loadRounds()
    const intervalId = setInterval(loadRounds, 30000)
    return () => clearInterval(intervalId)
  }, [loadHuayTypes, loadRounds])

  // อัปเดตรอบหวยแบบ real-time เมื่อ admin แก้ status (socket lottery_round_updated)
  useEffect(() => {
    const handleRoundUpdated = (e: Event) => {
      const customEvent = e as CustomEvent<{ round: LotteryRound; tenantId?: string }>
      const updated = customEvent.detail?.round
      if (!updated?._id) return
      // Refetch จาก API ด้วย cache-bust เพื่อให้ได้ข้อมูลล่าสุด
      loadRounds(true)
    }
    window.addEventListener('lottery_round_updated', handleRoundUpdated)
    return () => window.removeEventListener('lottery_round_updated', handleRoundUpdated)
  }, [loadRounds])

  // Update countdown every second
  useEffect(() => {
    const intervalId = setInterval(() => {
      const newCountdown: Record<string, string> = {}
      rounds.forEach(round => {
        newCountdown[round._id] = canRoundAcceptBet(round)
          ? lotteryService.formatTimeRemaining(round.betEndTime)
          : 'ปิดรับแทง'
      })
      setCountdown(newCountdown)
    }, 1000)

    return () => clearInterval(intervalId)
  }, [rounds])

  // Sort rounds: Circular order starting from current/closest round
  const sortedRounds = useMemo(() => {
    if (rounds.length === 0) return []

    // Extract round numbers and sort all rounds by round number
    const sortedByNumber = [...rounds].sort((a, b) => {
      const aNum = parseInt(a.roundNumber.split('-')[2] || '0', 10)
      const bNum = parseInt(b.roundNumber.split('-')[2] || '0', 10)
      return aNum - bNum
    })

    // Find the current/closest open round (earliest betEndTime among open rounds)
    const now = new Date()
    const openRounds = sortedByNumber
      .filter(r => r.status === 'open' && r.isOpen)
      .map(r => ({
        ...r,
        diff: new Date(r.betEndTime).getTime() - now.getTime(),
      }))
      .filter(r => r.diff > 0)

    if (openRounds.length === 0) {
      // No open rounds, just return sorted by number
      return sortedByNumber
    }

    // Find the closest open round (smallest diff)
    const currentRound = openRounds.sort((a, b) => a.diff - b.diff)[0]
    const currentRoundNum = parseInt(currentRound.roundNumber.split('-')[2] || '0', 10)

    // Split into two groups: from current to end, and from start to before current
    const fromCurrent = sortedByNumber.filter(r => {
      const num = parseInt(r.roundNumber.split('-')[2] || '0', 10)
      return num >= currentRoundNum
    })

    const beforeCurrent = sortedByNumber.filter(r => {
      const num = parseInt(r.roundNumber.split('-')[2] || '0', 10)
      return num < currentRoundNum
    })

    // Circular order: current -> end, then start -> before current
    return [...fromCurrent, ...beforeCurrent]
  }, [rounds])

  const yeekeeTypeImage = useMemo(() => {
    const match = huayTypes.find((huayType) =>
      ['หวยยี่กี', 'หวยยีกี่', 'ยี่กี', 'ยีกี่'].some((alias) => huayType.name?.includes(alias))
    )
    const image = String(match?.imageUrl || match?.imageURL || '').trim()
    return image || getImageUrl('logo.png')
  }, [huayTypes])

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row mx--1">
          <div className="col-12 px-1">
            <div className="w-100 p-2 bgc-4 bdr-5 wrap justify-content-between">
              <a 
                href="#"
                className="txt-link-d"
                onClick={(e) => {
                  e.preventDefault()
                  navigate('/lotto')
                }}
              >
                <i className="fas fa-chevron-left me-1"></i>
                ย้อนกลับ
              </a>
            </div>
          </div>

          <div className="col-12 mt-2 px-1">
            <motion.div 
              className="bgc-14 bdr-5 p-2 h-100"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <motion.div 
                className="wrap animateMe" 
                data-animation="fadeInUp"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="icon-f">
                  <img src={yeekeeTypeImage} alt="จับยี่กี" />
                </div>
                <h5 className="t-medium mx-2 c-white">จับยี่กี ({rounds.length} รอบ)</h5>
              </motion.div>

              <div className="wrap-box">
                <div className="row mx--1">
                  {loading ? (
                    <div className="col-12 text-center py-5">
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                      >
                        <i className="fas fa-spinner fa-spin fs-24 c-white"></i>
                        <p className="c-white mt-2">กำลังโหลดรอบยี่กี...</p>
                      </motion.div>
                    </div>
                  ) : sortedRounds.length > 0 ? (
                    sortedRounds.map((round, index) => (
                      <YeekeeCard
                        key={round._id}
                        round={round}
                        index={index}
                        countdown={countdown[round._id] || lotteryService.formatTimeRemaining(round.betEndTime)}
                        navigate={navigate}
                        urgentMinutesThreshold={urgentMinutesThreshold}
                      />
                    ))
                  ) : (
                    <motion.div 
                      className="col-12 text-center py-5"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <p className="c-white">ไม่มีรอบยี่กีในวันนี้</p>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default YeekeePage
